import React from 'react';
import InputMask from 'react-input-mask';
import unpick from '../../../utilities/unpick';
import { useMask, convertToPatternLikeValue, normalizeInitialValue } from './mask';
import ua from '../../../utilities/ua';
import trackEvent, { ANALYTICS_EVENTS } from '../../../utilities/analytics';

const hiddenMaskSupportedBrowsers = {
  // TODO: verify with older browsers
  'microsoft edge': 80,
  chrome: 78,
  safari: 12,
  firefox: 71,
};

/*
 * The component for hiding input value (input[type="password"])
 * that formats data before sending to the controller. It also removes special chars
 * that don't represent value before passing it to the input.value.
 * So if a client specifies a mask like "999-99-9999" and a user enters "1234",
 * the one would see in the input "****" while in the controller (form state)
 * we will store value "123-4"
 *
 * This component should be a used as a fallback for less modern browsers
 * where we are less sure in using `useMask` that doesn't use native HTML
 * input[type="password"].
 */
const HiddenMaskFallback = (props) => {
  const fieldProps = props.children.props;
  const inputRef = React.useRef();
  const { mask, value } = fieldProps;

  const displayValue = normalizeInitialValue(value, mask);

  const getValue = (event) => {
    return convertToPatternLikeValue(event.target.value, mask, false);
  };

  return React.cloneElement(props.children, {
    ref: inputRef,
    type: 'password',
    value: displayValue,
    autoComplete: 'off',
    'aria-autocomplete': 'off',
    onChange: (event) => {
      const end = inputRef.current.selectionEnd;
      fieldProps.onChange(event, getValue(event));
      setTimeout(() => {
        // here we want to restore the caret to the position
        // it can be wrong when a user types in the middle
        // and the text exceeds max length.
        // In this case the input value is shifted and trimmed from the end.
        // The browser in this case moves the caret to the end of the text input.
        // TODO: prevent caret blinking
        inputRef.current.selectionStart = end;
        inputRef.current.selectionEnd = end;
      });
    },
    onKeyDown: (e) => fieldProps.onKeyDown(e, getValue(e)),
    onKeyUp: (e) => fieldProps.onKeyUp(e, getValue(e)),
    onInput: (e) => fieldProps.onInput(e, getValue(e)),
    onPaste: (e) => fieldProps.onPaste(e, getValue(e)),
    onKeyPress: (e) => fieldProps.onKeyPress(e, getValue(e)),
    onBlur: (e) => fieldProps.onBlur(e, getValue(e)),
    onFocus: (e) => fieldProps.onFocus(e, getValue(e)),
  });
};

const HiddenMask = (props) => {
  const fieldProps = props.children.props;
  const { mask } = fieldProps;
  const ref = React.useRef();

  const { value, displayValue, formattedValue, onChange, onSelect } = useMask({
    pattern: mask,
    inputRef: ref,
    revealedValue: fieldProps.value,
    onChange: fieldProps.onChange,
  });

  return React.cloneElement(props.children, {
    ref,
    value: displayValue,
    onChange,
    onSelect,
    // pass revealed input value
    onKeyDown: (e) => fieldProps.onKeyDown(e, formattedValue),
    onKeyUp: (e) => fieldProps.onKeyUp(e, formattedValue),
    onInput: (e) => fieldProps.onInput(e, formattedValue),
    onPaste: (e) => fieldProps.onPaste(e, formattedValue),
    onKeyPress: (e) => fieldProps.onKeyPress(e, formattedValue),
    onBlur: (e) => fieldProps.onBlur(e, formattedValue),
    onFocus: (e) => fieldProps.onFocus(e, formattedValue),
  });
};

const InputMaskWrap = (props) => {
  const p = unpick(props.children.props, ['onPaste', 'onInput', 'onKeyPress', 'onKeyDown', 'onKeyUp']);

  return <InputMask {...p} />;
};

const isCustomPasswordMaskSupported = (() => {
  const { browser } = ua;
  const browserName = String(browser.name).toLowerCase();
  const minVersion = hiddenMaskSupportedBrowsers[browserName];
  if (!minVersion) {
    return false;
  }
  const [majorVersionStr] = String(browser.version).split('.');
  const majorVersion = Number(majorVersionStr);

  return majorVersion >= minVersion;
})();

let hiddenMaskNotified = false;

const ConditionalComponent = (props) => {
  React.useEffect(() => {
    if (props.hideValue && !hiddenMaskNotified) {
      hiddenMaskNotified = true;
      trackEvent({
        type: ANALYTICS_EVENTS.FEATURE_USED,
        feature: 'PasswordMask',
        status: 'Ok',
        args: {
          supported: isCustomPasswordMaskSupported,
          mask: props.children?.props?.mask,
        },
      });
    }
  }, [props.hideValue]);

  if (props.hideValue && isCustomPasswordMaskSupported) {
    return <HiddenMask {...props} />;
  } else if (props.hideValue) {
    return <HiddenMaskFallback {...props} />;
  } else {
    return <InputMaskWrap {...props} />;
  }
};

export default ConditionalComponent;
